import React, { useState, useEffect } from "react";
import { getUserSubs, updateSubscription } from "@api";
import { useUser, useMerchant, usePaymentSettings } from "@auth";
import { customersModule } from "@auth/modules";
import {
  readCustomer,
  readPaymentRequestMerchant,
  readCardPayout,
} from "@auth";
import {
  SectionHeader,
  SectionTitle,
  SectionTagline,
  FormGroup,
  SwitchLabel,
  SwitchTag,
  NotificationToggleRow,
  NotificationChannelColumns,
} from "../components";
import { useForm } from "react-hook-form";
import { useQueries, usePortalNotifications, useAiFeatures } from "@flags";

// Portal-destination field name for a notification type — kept alongside the existing Email
// field name (unchanged) so RHF tracks both toggles independently.
const portalFieldName = (type) => `${type}Portal`;

// Section heading. With the portal destination available it also carries the Email / Portal column
// labels (shown once, aligned above the switches). With the portal flag off it renders exactly the
// original bare heading so that view is unchanged.
function GroupHeading({ title, showChannels }) {
  if (!showChannels) {
    return <h4 className="font-semibold text-lg/6 mb-6">{title}</h4>;
  }

  return (
    <div className="w-full flex items-end justify-between mb-6">
      <h4 className="font-semibold text-lg/6">{title}</h4>
      <NotificationChannelColumns />
    </div>
  );
}

function Notifications() {
  const { hasModule, hasPermission } = useMerchant();
  const queriesEnabled = useQueries();
  const portalNotificationsEnabled = usePortalNotifications();
  const aiEnabled = useAiFeatures();
  const [userSubId, setUserSubId] = useState(null);
  const { user } = useUser();
  const { merchant } = useMerchant();
  const { paymentSettings } = usePaymentSettings();

  const posEnabled = !!paymentSettings?.terminals?.enabled;

  // Which optional sections render, in page order. Used both to render the section and to decide
  // whether it needs a divider — a section only gets a bottom border when a later one follows it,
  // so the last visible section never shows a trailing line.
  const showPos = posEnabled && hasPermission(readPaymentRequestMerchant);
  const showPayouts = hasPermission(readCardPayout);
  const showQueries =
    queriesEnabled && hasModule(customersModule) && hasPermission(readCustomer);
  const showNotesTasks = aiEnabled && portalNotificationsEnabled;

  const divider = (hasFollowing) =>
    `py-8 w-full flex flex-col items-start${hasFollowing ? " border-b border-zinc-200" : ""}`;

  const NOTIFICATION_TYPES = [
    "AllPayments",
    "MyRequests",
    "CustomerPayments",
    "Payouts",
    "TerminalPayments",
    "CustomerQueries",
    "NoteTagged",
    "TaskAssigned",
  ];

  const { control, setValue, watch } = useForm({
    defaultValues: NOTIFICATION_TYPES.reduce(
      (acc, type) => ({ ...acc, [type]: false, [portalFieldName(type)]: false }),
      {},
    ),
  });

  useEffect(() => {
    const fetchData = async () => {
      const data = await getUserSubs();
      setUserSubId(data.id);
      NOTIFICATION_TYPES.forEach((type) => {
        setValue(type, hasFlag(data, type, "Email"));
        setValue(portalFieldName(type), hasFlag(data, type, "Portal"));
      });
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue]);

  const hasFlag = (data, flag, destination = "Email") => {
    return (
      data.subscriptions.filter(
        (x) => x.type === flag && x.destination === destination && x.active,
      ).length > 0
    );
  };

  const onSubChange = async (name, value, destination = "Email") => {
    const fieldName = destination === "Portal" ? portalFieldName(name) : name;
    const exclusionFields =
      destination === "Portal"
        ? [portalFieldName("MyRequests"), portalFieldName("CustomerPayments")]
        : ["MyRequests", "CustomerPayments"];

    setValue(fieldName, value);

    if (name === "AllPayments" && value) {
      exclusionFields.forEach((field) => setValue(field, false));
    }

    await updateSubscription(userSubId, name, value, destination);

    if (name === "AllPayments" && value) {
      await updateSubscription(userSubId, "MyRequests", false, destination);
      await updateSubscription(userSubId, "CustomerPayments", false, destination);
    }
  };

  return (
    <div className="flex flex-col items-center py-4 px-5 border-b border-zinc-200">
      <div className="py-8 w-full max-w-4xl">
        <SectionHeader>
          <SectionTitle>Notifications</SectionTitle>
          <SectionTagline>
            {portalNotificationsEnabled
              ? "Customise the email and portal notifications you receive"
              : "Customise the email notifications you receive"}
          </SectionTagline>
          {user.memberships.length > 1 && (
            <SectionTagline>
              These changes apply to your account with <b>{merchant.name}</b>
            </SectionTagline>
          )}
        </SectionHeader>
        <div className={divider(showPos || showPayouts || showQueries || showNotesTasks)}>
          <GroupHeading title="Payment requests" showChannels={portalNotificationsEnabled} />
          <ul className="space-y-6 w-full">
            {hasPermission(readPaymentRequestMerchant) && (
              <li>
                <FormGroup>
                  <NotificationToggleRow
                    emailName="AllPayments"
                    portalName={portalFieldName("AllPayments")}
                    showPortal={portalNotificationsEnabled}
                    emailControl={control}
                    portalControl={control}
                    onEmailChange={(val) => onSubChange("AllPayments", val, "Email")}
                    onPortalChange={(val) => onSubChange("AllPayments", val, "Portal")}
                  >
                    <SwitchLabel>All payments</SwitchLabel>
                    <SwitchTag>
                      Receive a notification when any payment is made through
                      Ontik{posEnabled
                        ? ", except payments taken on your POS terminals (managed below)"
                        : ", regardless of channel"}
                    </SwitchTag>
                  </NotificationToggleRow>
                </FormGroup>
              </li>
            )}
            <li>
              <FormGroup>
                <NotificationToggleRow
                  emailName="MyRequests"
                  portalName={portalFieldName("MyRequests")}
                  showPortal={portalNotificationsEnabled}
                  emailControl={control}
                  portalControl={control}
                  emailDisabled={watch("AllPayments")}
                  portalDisabled={watch(portalFieldName("AllPayments"))}
                  onEmailChange={(val) => onSubChange("MyRequests", val, "Email")}
                  onPortalChange={(val) => onSubChange("MyRequests", val, "Portal")}
                >
                  <SwitchLabel>Payment requests</SwitchLabel>
                  <SwitchTag>
                    Receive a notification if a payment you requested through
                    Ontik has been paid
                  </SwitchTag>
                </NotificationToggleRow>
              </FormGroup>
            </li>
            {hasModule(customersModule) && hasPermission(readCustomer) && (
              <li>
                <FormGroup>
                  <NotificationToggleRow
                    emailName="CustomerPayments"
                    portalName={portalFieldName("CustomerPayments")}
                    showPortal={portalNotificationsEnabled}
                    emailControl={control}
                    portalControl={control}
                    emailDisabled={watch("AllPayments")}
                    portalDisabled={watch(portalFieldName("AllPayments"))}
                    onEmailChange={(val) => onSubChange("CustomerPayments", val, "Email")}
                    onPortalChange={(val) => onSubChange("CustomerPayments", val, "Portal")}
                  >
                    <SwitchLabel>Customer payments</SwitchLabel>
                    <SwitchTag>
                      Receive a notification when trade customers make a payment
                      through any Ontik channel
                    </SwitchTag>
                  </NotificationToggleRow>
                </FormGroup>
              </li>
            )}
          </ul>
        </div>

        {showPos && (
          <div className={divider(showPayouts || showQueries || showNotesTasks)}>
            <GroupHeading title="POS Terminals" showChannels={portalNotificationsEnabled} />
            <ul className="space-y-6 w-full">
              <li>
                <FormGroup>
                  <NotificationToggleRow
                    emailName="TerminalPayments"
                    portalName={portalFieldName("TerminalPayments")}
                    showPortal={portalNotificationsEnabled}
                    emailControl={control}
                    portalControl={control}
                    onEmailChange={(val) => onSubChange("TerminalPayments", val, "Email")}
                    onPortalChange={(val) => onSubChange("TerminalPayments", val, "Portal")}
                  >
                    <SwitchLabel>All POS payments</SwitchLabel>
                    <SwitchTag>
                      Receive a notification for every payment taken on your POS terminals.
                    </SwitchTag>
                  </NotificationToggleRow>
                </FormGroup>
              </li>
            </ul>
          </div>
        )}

        {showPayouts && (
          <div className={divider(showQueries || showNotesTasks)}>
            <GroupHeading title="Payouts" showChannels={portalNotificationsEnabled} />
            <ul className="space-y-6 w-full">
              <li>
                <FormGroup>
                  <NotificationToggleRow
                    emailName="Payouts"
                    portalName={portalFieldName("Payouts")}
                    showPortal={portalNotificationsEnabled}
                    emailControl={control}
                    portalControl={control}
                    onEmailChange={(val) => onSubChange("Payouts", val, "Email")}
                    onPortalChange={(val) => onSubChange("Payouts", val, "Portal")}
                  >
                    <SwitchLabel>All Payouts</SwitchLabel>
                    <SwitchTag>
                      Receive a notification when a payout from Adyen is made to
                      your bank account
                    </SwitchTag>
                  </NotificationToggleRow>
                </FormGroup>
              </li>
            </ul>
          </div>
        )}

        {showQueries && (
          <div className={divider(showNotesTasks)}>
            <GroupHeading title="Queries" showChannels={portalNotificationsEnabled} />
            <ul className="space-y-6 w-full">
              <li>
                <FormGroup>
                  <NotificationToggleRow
                    emailName="CustomerQueries"
                    portalName={portalFieldName("CustomerQueries")}
                    showPortal={portalNotificationsEnabled}
                    emailControl={control}
                    portalControl={control}
                    onEmailChange={(val) => onSubChange("CustomerQueries", val, "Email")}
                    onPortalChange={(val) => onSubChange("CustomerQueries", val, "Portal")}
                  >
                    <SwitchLabel>Query raised</SwitchLabel>
                    <SwitchTag>
                      Receive a notification when a customer raises a query on
                      an invoice
                    </SwitchTag>
                  </NotificationToggleRow>
                </FormGroup>
              </li>
            </ul>
          </div>
        )}

        {showNotesTasks && (
          <div className={divider(false)}>
            <GroupHeading title="Notes & tasks" showChannels={portalNotificationsEnabled} />
            <ul className="space-y-6 w-full">
              <li>
                <FormGroup>
                  <NotificationToggleRow
                    emailName="NoteTagged"
                    portalName={portalFieldName("NoteTagged")}
                    showPortal={portalNotificationsEnabled}
                    emailControl={control}
                    portalControl={control}
                    onEmailChange={(val) => onSubChange("NoteTagged", val, "Email")}
                    onPortalChange={(val) => onSubChange("NoteTagged", val, "Portal")}
                  >
                    <SwitchLabel>Note tags</SwitchLabel>
                    <SwitchTag>
                      Receive a notification when someone tags you in a note
                    </SwitchTag>
                  </NotificationToggleRow>
                </FormGroup>
              </li>
              <li>
                <FormGroup>
                  <NotificationToggleRow
                    emailName="TaskAssigned"
                    portalName={portalFieldName("TaskAssigned")}
                    showPortal={portalNotificationsEnabled}
                    emailControl={control}
                    portalControl={control}
                    onEmailChange={(val) => onSubChange("TaskAssigned", val, "Email")}
                    onPortalChange={(val) => onSubChange("TaskAssigned", val, "Portal")}
                  >
                    <SwitchLabel>Task assignments</SwitchLabel>
                    <SwitchTag>
                      Receive a notification when someone assigns a task to you
                    </SwitchTag>
                  </NotificationToggleRow>
                </FormGroup>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default Notifications;
